import React, { useState, useEffect, useRef, useMemo } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  CalendarDays, 
  Plus, 
  Clock,
  Users,
  Activity,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Upload,
  Trash2,
  FileSpreadsheet,
  X,
  Eye,
  Calendar,
  AlertTriangle,
  Lock,
  Unlock,
  Pencil,
  Save,
  Search
} from 'lucide-react';

export default function SlotsPage() {
  const { user } = useAuth();
  const ROLE_LEVELS = { master: 5, admin_geral: 4, admin: 3, marcador_geral: 2, marcador: 1 };
  const canEditSlots = (ROLE_LEVELS[user?.role] || 0) >= ROLE_LEVELS.admin_geral;
  const isAdmin = (ROLE_LEVELS[user?.role] || 0) >= ROLE_LEVELS.admin;
  const isAdminGeral = (ROLE_LEVELS[user?.role] || 0) >= ROLE_LEVELS.admin_geral;
  const [slots, setSlots] = useState([]);
  const [siglas, setSiglas] = useState({});
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadLabDialogOpen, setUploadLabDialogOpen] = useState(false);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayDetailOpen, setDayDetailOpen] = useState(false);
  const [viewMode, setViewMode] = useState('calendar');
  const [originTab, setOriginTab] = useState('hospital');
  const [searchTerm, setSearchTerm] = useState('');
  const labFileInputRef = useRef(null); // 'calendar' or 'list'
  const [currentMonth, setCurrentMonth] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });
  const fileInputRef = useRef(null);
  
  // Bloqueio de vagas (periodo geral)
  const [blockedPeriods, setBlockedPeriods] = useState([]);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  const [blockFormData, setBlockFormData] = useState({
    data_inicio: '',
    data_fim: '',
    motivo: '',
    tipo: 'dia',
    sigla: '',
    horario: '',
    origin: 'hospital'
  });

  // Edição de vagas por procedimento (nos cards)
  const [editProcDialog, setEditProcDialog] = useState(false);
  const [editProcSigla, setEditProcSigla] = useState('');
  const [editProcSlots, setEditProcSlots] = useState([]);
  const [editingSlot, setEditingSlot] = useState(null);
  const [editSlotForm, setEditSlotForm] = useState({
    profissional: '',
    data: '',
    horario: '',
    quantidade_total: 1,
    turno: 'M'
  });

  const [formData, setFormData] = useState({
    sigla: '',
    profissional: '',
    data: '',
    horario: '',
    quantidade_total: 1,
    turno: 'M'
  });

  useEffect(() => {
    fetchData();
  }, [currentMonth, originTab]);

  // Filtrar slots por origem (hospital ou lab_municipal)
  const filteredSlots = useMemo(() => {
    return slots.filter(s => {
      const slotOrigin = s.origin || 'hospital';
      return slotOrigin === originTab;
    });
  }, [slots, originTab]);

  // Filtrar siglas por origem
  const filteredSiglas = useMemo(() => {
    const result = {};
    Object.entries(siglas).forEach(([key, info]) => {
      const siglaOrigin = info.origin || 'hospital';
      if (siglaOrigin === originTab) {
        result[key] = info;
      }
    });
    return result;
  }, [siglas, originTab]);

  // Resultado da busca: vagas disponiveis no mes atual filtradas por sigla/nome
  const searchResults = useMemo(() => {
    const term = (searchTerm || '').trim().toLowerCase();
    if (!term) return null;
    
    // Vagas com qt_disp > 0
    const matches = filteredSlots.filter(s => {
      if (!s.quantidade_disponivel || s.quantidade_disponivel <= 0) return false;
      const sigla = (s.sigla || '').toLowerCase();
      const prof = (s.profissional || '').toLowerCase();
      const siglaInfo = filteredSiglas[s.sigla];
      const siglaName = (siglaInfo?.nome || '').toLowerCase();
      return sigla.includes(term) || prof.includes(term) || siglaName.includes(term);
    });
    
    // Agrupar por (sigla, data) e somar disponivel
    const grouped = {};
    matches.forEach(s => {
      const key = `${s.sigla}|${s.data}`;
      if (!grouped[key]) {
        grouped[key] = {
          sigla: s.sigla,
          data: s.data,
          siglaName: filteredSiglas[s.sigla]?.nome || s.sigla,
          horarios: [],
          totalDisponivel: 0,
        };
      }
      grouped[key].totalDisponivel += s.quantidade_disponivel;
      if (s.horario) {
        grouped[key].horarios.push({
          horario: s.horario,
          disponivel: s.quantidade_disponivel,
          total: s.quantidade_total,
          profissional: s.profissional,
        });
      }
    });
    
    // Ordenar por data
    const list = Object.values(grouped).sort((a, b) => a.data.localeCompare(b.data));
    list.forEach(g => g.horarios.sort((a, b) => (a.horario || '').localeCompare(b.horario || '')));
    return list;
  }, [searchTerm, filteredSlots, filteredSiglas]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const siglasRes = await api.get('/siglas');
      setSiglas(siglasRes.data && typeof siglasRes.data === 'object' && !Array.isArray(siglasRes.data) ? siglasRes.data : {});
      
      const mes = currentMonth.getMonth() + 1;
      const ano = currentMonth.getFullYear();
      const dataInicio = `${ano}-${String(mes).padStart(2, '0')}-01`;
      const dataFim = mes === 12 
        ? `${ano + 1}-01-01` 
        : `${ano}-${String(mes + 1).padStart(2, '0')}-01`;
      
      const [slotsRes, summaryRes, blockedRes] = await Promise.all([
        api.get(`/slots?data_inicio=${dataInicio}&data_fim=${dataFim}`),
        api.get(`/slots/summary?mes=${mes}&ano=${ano}&origin=${originTab}`),
        api.get('/blocked-periods')
      ]);
      // Blindagem: garante formatos esperados mesmo se a API retornar algo inesperado
      // (evita crash de render, ex: summary.map, durante race de renovacao de token)
      setSlots(Array.isArray(slotsRes.data) ? slotsRes.data : []);
      setSummary(Array.isArray(summaryRes.data) ? summaryRes.data : []);
      setBlockedPeriods(Array.isArray(blockedRes.data) ? blockedRes.data : []);
      
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/slots', { ...formData, origin: originTab });
      toast.success('Vaga criada com sucesso!');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar vaga');
    }
  };

  const resetForm = () => {
    setFormData({
      sigla: '',
      profissional: '',
      data: '',
      horario: '',
      quantidade_total: 1,
      turno: 'M'
    });
  };

  const handleDelete = async (slotId) => {
    if (!confirm('Tem certeza que deseja excluir esta vaga?')) return;
    
    try {
      await api.delete(`/slots/${slotId}`);
      toast.success('Vaga excluída com sucesso!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir vaga');
    }
  };

  const handleUploadCalendario = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const mes = currentMonth.getMonth() + 1;
    const ano = currentMonth.getFullYear();

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    formDataUpload.append('mes', mes.toString());
    formDataUpload.append('ano', ano.toString());

    const endpoint = originTab === 'lab_municipal' 
      ? '/slots/upload-calendario-lab' 
      : '/slots/upload-calendario';

    try {
      setUploading(true);
      const response = await api.post(endpoint, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const { vagas_importadas, resumo_por_sigla } = response.data;
      
      let mensagem = `${vagas_importadas} vagas importadas!\n\n`;
      Object.entries(resumo_por_sigla).forEach(([sigla, info]) => {
        mensagem += `${sigla}: ${info.quantidade} vagas em ${info.datas} dias\n`;
      });
      
      toast.success(mensagem);
      setUploadDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar calendario');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      if (labFileInputRef.current) {
        labFileInputRef.current.value = '';
      }
    }
  };

  const handleLimparMes = async () => {
    const mes = currentMonth.getMonth() + 1;
    const ano = currentMonth.getFullYear();
    const nomeMes = getMonthName();
    const localLabel = originTab === 'lab_municipal' ? 'Lab. Municipal' : 'Hospital';

    if (!confirm(`Tem certeza que deseja remover TODAS as vagas do ${localLabel} de ${nomeMes}?\n\nEsta ação não pode ser desfeita.`)) {
      return;
    }

    try {
      const response = await api.delete(`/slots/limpar-mes?mes=${mes}&ano=${ano}&origin=${originTab}`);
      toast.success(`${response.data.deleted_count} vagas do ${localLabel} removidas`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao limpar vagas');
    }
  };

  const handleDeleteAllSlots = async () => {
    const localLabel = originTab === 'lab_municipal' ? 'Lab. Municipal' : 'Hospital';
    try {
      const response = await api.delete(`/slots/limpar-todas?origin=${originTab}`);
      toast.success(`${response.data.deleted_count} vagas do ${localLabel} removidas`);
      setDeleteAllDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao deletar vagas');
    }
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentMonth);
    newDate.setDate(1);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentMonth(newDate);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const [ano, mes, dia] = dateStr.split('-');
    return `${dia}/${mes}/${ano}`;
  };

  const getMonthName = () => {
    return currentMonth.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  };

  // Agrupar slots por data
  const slotsByDate = useMemo(() => {
    return filteredSlots.reduce((acc, slot) => {
      if (!acc[slot.data]) {
        acc[slot.data] = [];
      }
      acc[slot.data].push(slot);
      return acc;
    }, {});
  }, [filteredSlots]);

  // Calcular estatísticas por dia para o calendário
  const dayStats = useMemo(() => {
    const stats = {};
    Object.entries(slotsByDate).forEach(([date, daySlots]) => {
      const total = daySlots.reduce((sum, s) => sum + s.quantidade_total, 0);
      const disponivel = daySlots.reduce((sum, s) => sum + s.quantidade_disponivel, 0);
      const ocupado = total - disponivel;
      const percentual = total > 0 ? Math.round((ocupado / total) * 100) : 0;
      const hasDeadlineBlocked = daySlots.some(s => s.deadline_blocked);
      stats[date] = { total, disponivel, ocupado, percentual, count: daySlots.length, hasDeadlineBlocked };
    });
    return stats;
  }, [slotsByDate]);

  // Gerar dias do calendário
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    // Primeiro dia do mês
    const firstDay = new Date(year, month, 1);
    // Último dia do mês
    const lastDay = new Date(year, month + 1, 0);
    
    // Dia da semana do primeiro dia (0 = Domingo)
    const startDayOfWeek = firstDay.getDay();
    
    // Total de dias no mês
    const totalDays = lastDay.getDate();
    
    const days = [];
    
    // Dias vazios no início
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push({ day: null, date: null });
    }
    
    // Dias do mês
    for (let day = 1; day <= totalDays; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      days.push({ 
        day, 
        date: dateStr,
        stats: dayStats[dateStr] || null
      });
    }
    
    return days;
  }, [currentMonth, dayStats]);

  // Obter cor de fundo baseado na ocupação
  const getOccupancyColor = (stats) => {
    if (!stats) return 'bg-slate-50 hover:bg-slate-100 border-slate-200';
    const { percentual } = stats;
    if (percentual === 0) return 'bg-green-50 hover:bg-green-100 border-green-300';
    if (percentual < 50) return 'bg-emerald-50 hover:bg-emerald-100 border-emerald-300';
    if (percentual < 80) return 'bg-amber-50 hover:bg-amber-100 border-amber-300';
    if (percentual < 100) return 'bg-orange-50 hover:bg-orange-100 border-orange-300';
    return 'bg-red-50 hover:bg-red-100 border-red-300';
  };

  // Obter cor do texto baseado na ocupação
  const getOccupancyTextColor = (stats) => {
    if (!stats) return 'text-slate-400';
    const { percentual } = stats;
    if (percentual === 0) return 'text-green-600';
    if (percentual < 50) return 'text-emerald-600';
    if (percentual < 80) return 'text-amber-600';
    if (percentual < 100) return 'text-orange-600';
    return 'text-red-600';
  };

  const handleDayClick = (date) => {
    if (date && slotsByDate[date]) {
      setSelectedDate(date);
      setDayDetailOpen(true);
    }
  };

  // Gerar set de datas bloqueadas para o calendário (somente bloqueios da origem ativa e do DIA INTEIRO)
  // Bloqueios por sigla ou com horário específico NÃO mascaram o dia no calendário visual
  const blockedDatesSet = useMemo(() => {
    const dates = new Set();
    blockedPeriods
      .filter(bp => (bp.origin || 'hospital') === originTab)
      .filter(bp => !bp.sigla && !bp.horario)
      .forEach(bp => {
        const start = new Date(bp.data_inicio + 'T12:00:00');
        const end = new Date(bp.data_fim + 'T12:00:00');
        let current = new Date(start);
        while (current <= end) {
          dates.add(current.toISOString().split('T')[0]);
          current.setDate(current.getDate() + 1);
        }
      });
    return dates;
  }, [blockedPeriods, originTab]);

  // Calcular data fim baseado no tipo de bloqueio
  const calcBlockEndDate = (startDate, tipo) => {
    if (!startDate) return '';
    const d = new Date(startDate + 'T12:00:00');
    switch (tipo) {
      case 'dia': return startDate;
      case 'semana': d.setDate(d.getDate() + 6); break;
      case 'quinzena': d.setDate(d.getDate() + 14); break;
      case 'mes': d.setMonth(d.getMonth() + 1); d.setDate(d.getDate() - 1); break;
      default: return startDate;
    }
    return d.toISOString().split('T')[0];
  };

  const handleBlockSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataFim = calcBlockEndDate(blockFormData.data_inicio, blockFormData.tipo);
      const payload = {
        ...blockFormData,
        data_fim: dataFim,
        sigla: blockFormData.sigla || null,
        horario: blockFormData.horario || null,
        origin: originTab
      };
      
      if (editingBlock) {
        await api.put(`/blocked-periods/${editingBlock.id}`, payload);
        toast.success('Bloqueio atualizado com sucesso!');
      } else {
        await api.post('/blocked-periods', payload);
        toast.success('Período bloqueado com sucesso!');
      }
      setBlockDialogOpen(false);
      setEditingBlock(null);
      setBlockFormData({ data_inicio: '', data_fim: '', motivo: '', tipo: 'dia', sigla: '', horario: '', origin: originTab });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar bloqueio');
    }
  };

  const handleEditBlock = (block) => {
    if (block.sigla && !block.horario) {
      // Bloqueios de procedimento por dia não têm edição (são instantâneos)
      return;
    }
    setEditingBlock(block);
    setBlockFormData({
      data_inicio: block.data_inicio,
      data_fim: block.data_fim,
      motivo: block.motivo || '',
      tipo: block.tipo || 'personalizado',
      sigla: block.sigla || '',
      horario: block.horario || '',
      origin: block.origin || 'hospital'
    });
    setBlockDialogOpen(true);
  };

  const handleNewBlock = () => {
    setEditingBlock(null);
    setBlockFormData({ data_inicio: '', data_fim: '', motivo: '', tipo: 'dia', sigla: '', horario: '', origin: originTab });
    setBlockDialogOpen(true);
  };

  const handleUnblock = async (periodId) => {
    if (!window.confirm('Deseja desbloquear este periodo?')) return;
    try {
      await api.delete(`/blocked-periods/${periodId}`);
      toast.success('Periodo desbloqueado com sucesso!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao desbloquear periodo');
    }
  };

  // Bloqueio por procedimento — limitado ao mês exibido (currentMonth)
  const handleBlockProc = async (sigla) => {
    const ano = currentMonth.getFullYear();
    const mes = currentMonth.getMonth() + 1; // 1-12
    const ultimoDia = new Date(ano, mes, 0).getDate();
    const data_inicio = `${ano}-${String(mes).padStart(2, '0')}-01`;
    const data_fim = `${ano}-${String(mes).padStart(2, '0')}-${String(ultimoDia).padStart(2, '0')}`;
    const nomeMes = currentMonth.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    if (!window.confirm(`Bloquear ${sigla} apenas em ${nomeMes}?`)) return;
    try {
      await api.post('/blocked-periods', {
        data_inicio,
        data_fim,
        motivo: `Procedimento ${sigla} bloqueado em ${nomeMes}`,
        tipo: 'mes',
        sigla: sigla,
        origin: originTab
      });
      toast.success(`${sigla} bloqueado em ${nomeMes}!`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao bloquear');
    }
  };

  const handleUnblockProc = async (sigla) => {
    const procBlocks = blockedPeriods.filter(bp => bp.sigla === sigla && (bp.origin || 'hospital') === originTab);
    if (procBlocks.length === 0) return;
    try {
      for (const bp of procBlocks) {
        await api.delete(`/blocked-periods/${bp.id}`);
      }
      toast.success(`${sigla} desbloqueado!`);
      fetchData();
    } catch (error) {
      toast.error('Erro ao desbloquear');
    }
  };

  const isProcBlocked = (sigla) => {
    return blockedPeriods.some(bp => bp.sigla === sigla && (bp.origin || 'hospital') === originTab);
  };

  // Editar vagas pelo card do procedimento
  const handleOpenEditProc = (sigla) => {
    setEditProcSigla(sigla);
    const procSlots = filteredSlots.filter(s => s.sigla === sigla);
    setEditProcSlots(procSlots);
    setEditingSlot(null);
    setEditProcDialog(true);
  };

  const handleStartEditSlot = (slot) => {
    setEditingSlot(slot);
    setEditSlotForm({
      profissional: slot.profissional || '',
      data: slot.data || '',
      horario: slot.horario || '',
      quantidade_total: slot.quantidade_total || 1,
      turno: slot.turno || 'M'
    });
  };

  const handleSaveSlotEdit = async () => {
    if (!editingSlot) return;
    try {
      await api.put(`/slots/${editingSlot.id}`, {
        sigla: editProcSigla,
        ...editSlotForm
      });
      toast.success('Vaga atualizada!');
      setEditingSlot(null);
      fetchData();
      setEditProcDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar vaga');
    }
  };

  const handleDeleteSlotFromCard = async (slotId) => {
    if (!window.confirm('Excluir esta vaga?')) return;
    try {
      await api.delete(`/slots/${slotId}`);
      toast.success('Vaga excluida!');
      setEditProcSlots(prev => prev.filter(s => s.id !== slotId));
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir vaga');
    }
  };

  const handleAddSlotForDay = (date) => {
    setFormData(prev => ({ ...prev, data: date }));
    setDayDetailOpen(false);
    setDialogOpen(true);
  };

  const sortedDates = Object.keys(slotsByDate).sort();

  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  return (
    <div data-testid="slots-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2 tracking-tight">Calendário de Vagas</h1>
          <p className="text-slate-600">
            {canEditSlots ? 'Gerencie as vagas disponiveis para marcacoes' : 'Visualize as vagas disponiveis para marcacoes'}
          </p>
        </div>
        {canEditSlots && (
        <div className="flex gap-3">
          {isAdminGeral && (
            <Button 
              onClick={handleNewBlock}
              variant="outline"
              className="border-amber-200 text-amber-700 hover:bg-amber-50"
              data-testid="block-period-button"
            >
              <Lock className="h-4 w-4 mr-2" />
              Bloquear Periodo
            </Button>
          )}
          {isAdmin && (
            <Button 
              onClick={() => setDeleteAllDialogOpen(true)}
              variant="outline"
              className="border-red-200 text-red-600 hover:bg-red-50"
              data-testid="delete-all-slots-button"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Deletar Todas
            </Button>
          )}
          <Button 
            onClick={() => setUploadDialogOpen(true)}
            variant="outline"
            className="border-teal-200 text-teal-700 hover:bg-teal-50"
            data-testid="upload-calendario-button"
          >
            <Upload className="h-4 w-4 mr-2" />
            {originTab === 'lab_municipal' ? 'Importar Calendário Lab' : 'Importar Calendário'}
          </Button>
          <Button 
            onClick={() => setDialogOpen(true)}
            className="bg-teal-700 hover:bg-teal-800"
            data-testid="add-slot-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nova Vaga
          </Button>
        </div>
        )}
      </div>

      {/* Abas Hospital / Lab Municipal */}
      <div className="mb-4">
        <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl border border-slate-200">
          <button
            onClick={() => setOriginTab('hospital')}
            data-testid="origin-tab-hospital"
            className={`flex-1 flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${
              originTab === 'hospital'
                ? 'bg-teal-700 text-white shadow-md'
                : 'text-slate-500 hover:bg-white hover:text-slate-700'
            }`}
          >
            Hospital
          </button>
          <button
            onClick={() => setOriginTab('lab_municipal')}
            data-testid="origin-tab-lab"
            className={`flex-1 flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${
              originTab === 'lab_municipal'
                ? 'bg-amber-600 text-white shadow-md'
                : 'text-slate-500 hover:bg-white hover:text-slate-700'
            }`}
          >
            Lab. Municipal
          </button>
        </div>
      </div>

      {/* Navegação do Mês */}
      <div className="flex items-center justify-between bg-white p-4 rounded-xl border border-slate-200 shadow-sm mb-6">
        <Button variant="outline" onClick={() => navigateMonth(-1)}>
          <ChevronLeft className="h-4 w-4 mr-1" />
          Mês Anterior
        </Button>
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-semibold text-slate-900 capitalize">
            <CalendarDays className="h-5 w-5 inline mr-2 text-teal-600" />
            {getMonthName()}
          </h2>
          {/* Toggle de Visualização */}
          <div className="flex bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('calendar')}
              className={`px-3 py-1.5 text-sm rounded-md transition-all ${
                viewMode === 'calendar' 
                  ? 'bg-white text-teal-700 shadow-sm font-medium' 
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Calendar className="h-4 w-4 inline mr-1" />
              Calendário
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1.5 text-sm rounded-md transition-all ${
                viewMode === 'list' 
                  ? 'bg-white text-teal-700 shadow-sm font-medium' 
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Eye className="h-4 w-4 inline mr-1" />
              Lista
            </button>
          </div>
        </div>
        <Button variant="outline" onClick={() => navigateMonth(1)}>
          Próximo Mês
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>

      {/* Busca de Vagas no calendario */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Buscar vaga por sigla, nome ou profissional (ex: BAAR, USG, Dr Joao)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-vagas-input"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              data-testid="clear-search-btn"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {searchResults && (
          <div className="mt-4" data-testid="search-results">
            {searchResults.length === 0 ? (
              <p className="text-sm text-slate-500 italic">
                Nenhuma vaga disponivel para "{searchTerm}" em {getMonthName()}.
              </p>
            ) : (
              <div>
                <p className="text-xs font-medium text-slate-600 mb-2">
                  {searchResults.length} {searchResults.length === 1 ? 'dia com vaga disponivel' : 'dias com vagas disponiveis'} em {getMonthName()}:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-72 overflow-y-auto">
                  {searchResults.map((g) => {
                    const dt = new Date(g.data + 'T00:00:00');
                    const diaSem = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'][dt.getDay()];
                    const dia = String(dt.getDate()).padStart(2, '0');
                    const mes = String(dt.getMonth() + 1).padStart(2, '0');
                    return (
                      <button
                        key={`${g.sigla}-${g.data}`}
                        onClick={() => { setSelectedDate(g.data); setDayDetailOpen(true); }}
                        className="text-left bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded-lg p-3 transition-colors"
                        data-testid={`search-result-${g.sigla}-${g.data}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-bold text-teal-900">
                            {diaSem}, {dia}/{mes}
                          </span>
                          <span className="text-xs px-1.5 py-0.5 bg-teal-700 text-white rounded font-medium">
                            {g.totalDisponivel}
                          </span>
                        </div>
                        <p className="text-xs text-slate-700 font-medium truncate">{g.sigla}</p>
                        {g.horarios.length > 0 && (
                          <p className="text-[11px] text-slate-500 truncate mt-0.5">
                            {g.horarios.map((h) => `${h.horario} (${h.disponivel})`).slice(0, 4).join(', ')}
                            {g.horarios.length > 4 ? '...' : ''}
                          </p>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Legenda de Cores */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm mb-6">
        <div className="flex flex-wrap items-center gap-4 justify-center">
          <span className="text-sm text-slate-600 font-medium">Legenda:</span>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-slate-100 border border-slate-300"></div>
            <span className="text-xs text-slate-600">Sem vagas</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-green-100 border border-green-400"></div>
            <span className="text-xs text-slate-600">0% ocupado</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-emerald-100 border border-emerald-400"></div>
            <span className="text-xs text-slate-600">&lt;50%</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-amber-100 border border-amber-400"></div>
            <span className="text-xs text-slate-600">50-80%</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-orange-100 border border-orange-400"></div>
            <span className="text-xs text-slate-600">80-99%</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-red-100 border border-red-400"></div>
            <span className="text-xs text-slate-600">100% (lotado)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-red-200 border border-red-500 flex items-center justify-center">
              <Lock className="h-2.5 w-2.5 text-red-600" />
            </div>
            <span className="text-xs text-slate-600">Bloqueado</span>
          </div>
        </div>
      </div>

      {/* Resumo por Sigla */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Resumo do Mês</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {summary.map((item) => {
            const blocked = isProcBlocked(item.sigla);
            return (
            <div 
              key={item.sigla} 
              className={`bg-white p-4 rounded-xl border shadow-sm hover:shadow-md transition-shadow ${blocked ? 'border-red-300 bg-red-50/30' : 'border-slate-200'}`}
              data-testid={`summary-card-${item.sigla}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className={`px-2 py-1 rounded text-xs font-bold ${blocked ? 'bg-red-100 text-red-800' : 'bg-teal-100 text-teal-800'}`}>
                  {item.sigla}
                </span>
                {blocked && (
                  <Lock className="h-3.5 w-3.5 text-red-500" />
                )}
              </div>
              <p className="text-xs text-slate-500 mb-2 truncate">{item.sigla_info?.nome}</p>
              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Total:</span>
                  <span className="font-semibold">{item.total_vagas}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-green-600">Disponiveis:</span>
                  <span className="font-semibold text-green-600">{item.vagas_disponiveis}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-amber-600">Ocupadas:</span>
                  <span className="font-semibold text-amber-600">{item.vagas_ocupadas}</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2 mt-2">
                  <div 
                    className={`h-2 rounded-full transition-all ${
                      item.percentual_ocupacao === 0 ? 'bg-green-500' :
                      item.percentual_ocupacao < 50 ? 'bg-emerald-500' :
                      item.percentual_ocupacao < 80 ? 'bg-amber-500' :
                      item.percentual_ocupacao < 100 ? 'bg-orange-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${item.percentual_ocupacao}%` }}
                  />
                </div>
                <p className="text-xs text-center text-slate-500">{item.percentual_ocupacao}% ocupado</p>
              </div>
              {canEditSlots && (
                <div className="mt-2 pt-2 border-t border-slate-100 flex gap-1.5">
                  <button
                    onClick={() => handleOpenEditProc(item.sigla)}
                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                    title="Editar vagas"
                    data-testid={`edit-proc-${item.sigla}`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  {blocked ? (
                    <button
                      onClick={() => handleUnblockProc(item.sigla)}
                      className="p-1.5 text-green-600 hover:bg-green-50 rounded-md transition-colors"
                      title="Desbloquear"
                      data-testid={`unblock-proc-${item.sigla}`}
                    >
                      <Unlock className="h-3.5 w-3.5" />
                    </button>
                  ) : (
                    <button
                      onClick={() => handleBlockProc(item.sigla)}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                      title="Bloquear"
                      data-testid={`block-proc-${item.sigla}`}
                    >
                      <Lock className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              )}
            </div>
            );
          })}
          {summary.length === 0 && (
            <div className="col-span-full text-center py-8 text-slate-500">
              <CalendarDays className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              Nenhuma vaga cadastrada para este mês
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-500">
          <Activity className="h-8 w-8 animate-spin mx-auto mb-4 text-teal-600" />
          Carregando...
        </div>
      ) : viewMode === 'calendar' ? (
        /* Calendário Visual Interativo */
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-200 bg-slate-50">
            <h3 className="font-semibold text-slate-900">Calendário Interativo</h3>
            <p className="text-sm text-slate-500">Clique em um dia para ver e gerenciar as vagas</p>
          </div>
          
          {/* Cabeçalho dos dias da semana */}
          <div className="grid grid-cols-7 border-b border-slate-200">
            {weekDays.map((day, index) => (
              <div 
                key={day} 
                className={`p-3 text-center text-sm font-semibold ${
                  index === 0 ? 'text-red-500' : 'text-slate-700'
                } bg-slate-50`}
              >
                {day}
              </div>
            ))}
          </div>
          
          {/* Grade do Calendário */}
          <div className="grid grid-cols-7">
            {calendarDays.map((dayInfo, index) => (
              <div
                key={index}
                onClick={() => dayInfo.date && handleDayClick(dayInfo.date)}
                className={`
                  min-h-[100px] p-2 border-b border-r border-slate-100 
                  ${dayInfo.day && blockedDatesSet.has(dayInfo.date) 
                    ? 'bg-red-100 hover:bg-red-200 border-red-300' 
                    : dayInfo.day ? getOccupancyColor(dayInfo.stats) : 'bg-slate-50'}
                  ${dayInfo.day && (dayInfo.stats || blockedDatesSet.has(dayInfo.date)) ? 'cursor-pointer' : ''}
                  ${!dayInfo.day ? '' : 'hover:shadow-inner'}
                  transition-all
                `}
              >
                {dayInfo.day && (
                  <>
                    <div className={`font-semibold text-lg mb-1 ${
                      index % 7 === 0 ? 'text-red-500' : 'text-slate-800'
                    }`}>
                      {dayInfo.day}
                      {blockedDatesSet.has(dayInfo.date) && (
                        <Lock className="h-3.5 w-3.5 inline ml-1 text-red-500" />
                      )}
                    </div>
                    {blockedDatesSet.has(dayInfo.date) ? (
                      <div className="text-xs text-red-600 font-medium mt-1">
                        Bloqueado
                      </div>
                    ) : dayInfo.stats ? (
                      <div className="space-y-1">
                        <div className={`text-xs font-medium ${getOccupancyTextColor(dayInfo.stats)}`}>
                          {dayInfo.stats.count} tipo(s)
                        </div>
                        <div className="flex items-center gap-1 text-xs">
                          <Users className="h-3 w-3 text-slate-400" />
                          <span className="text-green-600 font-medium">{dayInfo.stats.disponivel}</span>
                          <span className="text-slate-400">/</span>
                          <span className="text-slate-600">{dayInfo.stats.total}</span>
                        </div>
                        <div className="w-full bg-white/50 rounded-full h-1.5">
                          <div 
                            className={`h-1.5 rounded-full ${
                              dayInfo.stats.percentual === 0 ? 'bg-green-500' :
                              dayInfo.stats.percentual < 50 ? 'bg-emerald-500' :
                              dayInfo.stats.percentual < 80 ? 'bg-amber-500' :
                              dayInfo.stats.percentual < 100 ? 'bg-orange-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${dayInfo.stats.percentual}%` }}
                          />
                        </div>
                        <div className={`text-xs font-medium ${getOccupancyTextColor(dayInfo.stats)}`}>
                          {dayInfo.stats.percentual}% ocupado
                        </div>
                      </div>
                    ) : (
                      <div className="text-xs text-slate-400 mt-2">
                        Sem vagas
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        /* Lista de Vagas por Data */
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-200 bg-slate-50">
            <h3 className="font-semibold text-slate-900">Vagas do Mês</h3>
          </div>
          
          {sortedDates.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              Nenhuma vaga cadastrada
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {sortedDates.map((data) => (
                <div key={data} className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <CalendarDays className="h-5 w-5 text-teal-600" />
                      <span className="font-semibold text-slate-900">{formatDate(data)}</span>
                      <span className="text-sm text-slate-500">
                        ({new Date(data + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long' })})
                      </span>
                    </div>
                    {dayStats[data] && (
                      <span className={`text-sm font-medium ${getOccupancyTextColor(dayStats[data])}`}>
                        {dayStats[data].disponivel}/{dayStats[data].total} vagas disponíveis
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {slotsByDate[data].map((slot) => (
                      <div 
                        key={slot.id}
                        className={`p-3 rounded-lg border ${
                          slot.deadline_blocked
                            ? 'bg-amber-50 border-amber-300'
                            : slot.quantidade_disponivel > 0 
                              ? 'bg-green-50 border-green-200' 
                              : 'bg-red-50 border-red-200'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="px-2 py-1 bg-white rounded text-xs font-bold border">
                            {slot.sigla}
                          </span>
                          <div className="flex items-center gap-2">
                            {slot.deadline_blocked && (
                              <span className="flex items-center text-xs text-amber-700 font-medium bg-amber-100 px-2 py-0.5 rounded">
                                <Lock className="h-3 w-3 mr-1" />
                                Prazo D-2
                              </span>
                            )}
                            <span className="flex items-center text-xs text-slate-600">
                              <Clock className="h-3 w-3 mr-1" />
                              {slot.horario || (slot.turno === 'M' ? 'Manhã' : 'Tarde')}
                            </span>
                          </div>
                        </div>
                        <p className="text-sm font-medium text-slate-900 mb-1">
                          {slot.sigla_info?.nome}
                        </p>
                        <p className="text-xs text-slate-600 mb-2">
                          Prof: {slot.profissional}
                        </p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1">
                            <Users className="h-4 w-4 text-slate-500" />
                            <span className={`text-sm font-semibold ${
                              slot.quantidade_disponivel > 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {slot.quantidade_disponivel}/{slot.quantidade_total}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            {slot.deadline_blocked && isAdminGeral && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={async () => {
                                  try {
                                    await api.post(`/slots/${slot.id}/unblock-deadline`);
                                    toast.success('Vaga desbloqueada!');
                                    fetchData();
                                  } catch (e) {
                                    toast.error(e.response?.data?.detail || 'Erro ao desbloquear');
                                  }
                                }}
                                className="h-7 text-xs text-amber-600 hover:bg-amber-50"
                                data-testid={`unblock-deadline-${slot.id}`}
                              >
                                <Unlock className="h-3 w-3 mr-1" />
                                Desbloquear
                              </Button>
                            )}
                            {!slot.deadline_blocked && slot.deadline_unblocked_by && isAdminGeral && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={async () => {
                                  try {
                                    await api.post(`/slots/${slot.id}/reblock-deadline`);
                                    toast.success('Vaga bloqueada novamente!');
                                    fetchData();
                                  } catch (e) {
                                    toast.error(e.response?.data?.detail || 'Erro ao bloquear');
                                  }
                                }}
                                className="h-7 text-xs text-red-600 hover:bg-red-50"
                                data-testid={`reblock-deadline-${slot.id}`}
                              >
                                <Lock className="h-3 w-3 mr-1" />
                                Bloquear
                              </Button>
                            )}
                            {canEditSlots && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleDelete(slot.id)}
                              className="h-7 text-xs text-red-600 hover:bg-red-50"
                            >
                              Excluir
                            </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal de Detalhes do Dia */}
      <Dialog open={dayDetailOpen} onOpenChange={setDayDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center gap-2">
              <CalendarDays className="h-6 w-6 text-teal-600" />
              {selectedDate && (
                <>
                  {formatDate(selectedDate)} 
                  <span className="text-base font-normal text-slate-500">
                    ({new Date(selectedDate + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long' })})
                  </span>
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedDate && slotsByDate[selectedDate] && (
            <div className="mt-4 space-y-4">
              {/* Resumo do Dia */}
              {dayStats[selectedDate] && (
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm text-slate-600">Total de vagas:</span>
                      <span className="ml-2 font-semibold">{dayStats[selectedDate].total}</span>
                    </div>
                    <div>
                      <span className="text-sm text-green-600">Disponíveis:</span>
                      <span className="ml-2 font-semibold text-green-600">{dayStats[selectedDate].disponivel}</span>
                    </div>
                    <div>
                      <span className="text-sm text-amber-600">Ocupadas:</span>
                      <span className="ml-2 font-semibold text-amber-600">{dayStats[selectedDate].ocupado}</span>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                      dayStats[selectedDate].percentual === 0 ? 'bg-green-100 text-green-700' :
                      dayStats[selectedDate].percentual < 50 ? 'bg-emerald-100 text-emerald-700' :
                      dayStats[selectedDate].percentual < 80 ? 'bg-amber-100 text-amber-700' :
                      dayStats[selectedDate].percentual < 100 ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {dayStats[selectedDate].percentual}% ocupado
                    </div>
                  </div>
                </div>
              )}

              {/* Lista de Vagas do Dia */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {slotsByDate[selectedDate].map((slot) => (
                  <div 
                    key={slot.id}
                    className={`p-4 rounded-lg border-2 ${
                      slot.quantidade_disponivel > 0 
                        ? 'bg-green-50 border-green-300' 
                        : 'bg-red-50 border-red-300'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="px-3 py-1 bg-white rounded-lg text-sm font-bold border shadow-sm">
                        {slot.sigla}
                      </span>
                      <span className="flex items-center text-sm text-slate-600 bg-white px-2 py-1 rounded border">
                        <Clock className="h-4 w-4 mr-1" />
                        {slot.horario || (slot.turno === 'M' ? 'Manhã' : 'Tarde')}
                      </span>
                    </div>
                    <p className="font-semibold text-slate-900 mb-1">
                      {slot.sigla_info?.nome}
                    </p>
                    <p className="text-sm text-slate-600 mb-3">
                      Profissional: {slot.profissional}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Users className="h-5 w-5 text-slate-500" />
                        <span className={`text-lg font-bold ${
                          slot.quantidade_disponivel > 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {slot.quantidade_disponivel}
                        </span>
                        <span className="text-slate-400">/</span>
                        <span className="text-slate-600">{slot.quantidade_total}</span>
                        <span className="text-sm text-slate-500">vagas</span>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleDelete(slot.id)}
                        className="text-red-600 hover:bg-red-50 border-red-200"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Excluir
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Botão para adicionar nova vaga neste dia */}
              {canEditSlots && (
              <div className="flex justify-center pt-4 border-t">
                <Button 
                  onClick={() => handleAddSlotForDay(selectedDate)}
                  className="bg-teal-700 hover:bg-teal-800"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Adicionar Vaga neste Dia
                </Button>
              </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Nova Vaga */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Nova Vaga</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Sigla do Procedimento *</Label>
                <Select value={formData.sigla} onValueChange={(v) => setFormData({...formData, sigla: v})}>
                  <SelectTrigger className="h-11 rounded-lg" data-testid="slot-sigla-select">
                    <SelectValue placeholder="Selecione a sigla" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(filteredSiglas).map(([sigla, info]) => (
                      <SelectItem key={sigla} value={sigla}>
                        {sigla} - {info.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-2">
                <Label>Profissional *</Label>
                <Input
                  value={formData.profissional}
                  onChange={(e) => setFormData({...formData, profissional: e.target.value})}
                  placeholder="Nome do profissional"
                  required
                  className="h-11 rounded-lg"
                />
              </div>

              <div>
                <Label>Data *</Label>
                <Input
                  type="date"
                  value={formData.data}
                  onChange={(e) => setFormData({...formData, data: e.target.value})}
                  required
                  className="h-11 rounded-lg"
                />
              </div>

              <div>
                <Label>Horário</Label>
                <Input
                  type="time"
                  value={formData.horario}
                  onChange={(e) => setFormData({...formData, horario: e.target.value})}
                  className="h-11 rounded-lg"
                />
              </div>

              <div>
                <Label>Turno *</Label>
                <Select value={formData.turno} onValueChange={(v) => setFormData({...formData, turno: v})}>
                  <SelectTrigger className="h-11 rounded-lg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M">Manhã</SelectItem>
                    <SelectItem value="T">Tarde</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Quantidade de Vagas *</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.quantidade_total}
                  onChange={(e) => setFormData({...formData, quantidade_total: parseInt(e.target.value) || 1})}
                  required
                  className="h-11 rounded-lg"
                />
              </div>
            </div>

            <div className="flex gap-3 justify-end pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-teal-700 hover:bg-teal-800">
                <Plus className="h-4 w-4 mr-2" />
                Criar Vaga
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de Upload de Calendário */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center gap-2">
              <FileSpreadsheet className="h-6 w-6 text-teal-600" />
              Importar Calendário
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            <div className="p-4 bg-sky-50 rounded-lg border border-sky-200">
              <h4 className="font-semibold text-sky-900 mb-2">Instruções</h4>
              <ul className="text-sm text-sky-700 space-y-1">
                <li>• Selecione o arquivo Excel (.xls ou .xlsx) do calendário de regulação</li>
                <li>• O sistema irá extrair automaticamente as vagas para o mês selecionado</li>
                <li>• As siglas serão identificadas e associadas aos procedimentos cadastrados</li>
              </ul>
            </div>

            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>Mês de Importação:</strong> {getMonthName()}
              </p>
              <p className="text-xs text-amber-600 mt-1">
                Use os botões de navegação na tela principal para alterar o mês.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Arquivo do Calendário {originTab === 'lab_municipal' ? '(Word .docx)' : '(Excel .xls/.xlsx)'}</Label>
              <Input
                ref={originTab === 'lab_municipal' ? labFileInputRef : fileInputRef}
                type="file"
                accept={originTab === 'lab_municipal' ? '.docx,.doc' : '.xls,.xlsx'}
                onChange={handleUploadCalendario}
                disabled={uploading}
                className="h-11 rounded-lg cursor-pointer"
              />
            </div>

            {uploading && (
              <div className="flex items-center justify-center py-4">
                <Activity className="h-6 w-6 animate-spin text-teal-600 mr-2" />
                <span className="text-slate-600">Processando calendário...</span>
              </div>
            )}

            <div className="flex gap-3 justify-between pt-4 border-t">
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleLimparMes}
                className="text-red-600 hover:bg-red-50 border-red-200"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Limpar Mês
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setUploadDialogOpen(false)}
              >
                Fechar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmação - Deletar TODAS as Vagas */}
      <Dialog open={deleteAllDialogOpen} onOpenChange={setDeleteAllDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-red-600 flex items-center gap-2">
              <AlertTriangle className="h-6 w-6" />
              Deletar Vagas - {originTab === 'lab_municipal' ? 'Lab. Municipal' : 'Hospital'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <p className="text-red-800 font-semibold mb-2">ATENÇÃO!</p>
              <p className="text-sm text-red-700">
                Esta ação irá remover TODAS as vagas {originTab === 'lab_municipal' ? 'do Laboratório Municipal' : 'do Hospital'}, de TODOS os meses.
              </p>
              <p className="text-sm text-red-700 mt-2 font-semibold">
                Esta ação NÃO pode ser desfeita!
              </p>
            </div>

            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                Se existirem consultas marcadas vinculadas às vagas, a operação será bloqueada para evitar inconsistências.
              </p>
            </div>

            <div className="flex gap-3 justify-end pt-4 border-t">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDeleteAllDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button 
                type="button" 
                onClick={handleDeleteAllSlots}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-all-button"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Sim, Deletar Todas
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Secao de Periodos Bloqueados */}
      {(() => {
        const filteredBlocks = blockedPeriods.filter(bp => (bp.origin || 'hospital') === originTab);
        return filteredBlocks.length > 0 && (
        <div className="mt-6 bg-white rounded-xl border border-red-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-red-100 bg-red-50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-red-600" />
              <h3 className="font-semibold text-red-900">Períodos Bloqueados · {originTab === 'lab_municipal' ? 'Lab. Municipal' : 'Hospital'}</h3>
            </div>
            <span className="text-sm text-red-600">{filteredBlocks.length} bloqueio(s) ativo(s)</span>
          </div>
          <div className="divide-y divide-red-100">
            {filteredBlocks.map((bp) => {
              const dataInicio = new Date(bp.data_inicio + 'T12:00:00');
              const dataFim = new Date(bp.data_fim + 'T12:00:00');
              return (
                <div key={bp.id} className="px-4 py-3 flex items-center justify-between hover:bg-red-50/50 transition-colors" data-testid={`blocked-period-${bp.id}`}>
                  <div className="flex items-center gap-4">
                    <div className="px-3 py-1 bg-red-100 text-red-800 rounded-lg text-xs font-bold uppercase">
                      {bp.tipo}
                    </div>
                    {bp.sigla ? (
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded text-xs font-bold">{bp.sigla}</span>
                    ) : (
                      <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs font-medium">Todos</span>
                    )}
                    {bp.horario && (
                      <span className="px-2 py-0.5 bg-purple-100 text-purple-800 rounded text-xs font-bold" data-testid={`block-horario-${bp.id}`}>
                        {bp.horario}
                      </span>
                    )}
                    <div>
                      <div className="font-medium text-slate-900">
                        {dataInicio.toLocaleDateString('pt-BR')} - {dataFim.toLocaleDateString('pt-BR')}
                      </div>
                      {bp.motivo && (
                        <p className="text-sm text-slate-500">{bp.motivo}</p>
                      )}
                    </div>
                  </div>
                  {canEditSlots && (
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditBlock(bp)}
                        className="text-blue-600 hover:bg-blue-50 border-blue-200"
                        data-testid={`edit-block-${bp.id}`}
                      >
                        <Pencil className="h-4 w-4 mr-1" />
                        Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleUnblock(bp.id)}
                        className="text-green-600 hover:bg-green-50 border-green-200"
                        data-testid={`unblock-${bp.id}`}
                      >
                        <Unlock className="h-4 w-4 mr-1" />
                        Desbloquear
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        );
      })()}

      {/* Modal de Bloqueio de Periodo (geral) */}
      <Dialog open={blockDialogOpen} onOpenChange={(open) => { setBlockDialogOpen(open); if (!open) setEditingBlock(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center gap-2">
              <Lock className="h-6 w-6 text-amber-600" />
              {editingBlock ? 'Editar Bloqueio' : 'Bloquear Periodo'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleBlockSubmit} className="space-y-4 mt-4">
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                Bloqueando para <strong>{originTab === 'lab_municipal' ? 'Laboratório Municipal' : 'Hospital'}</strong>. Opcionalmente restrinja a um procedimento (sigla) e um horário específico.
              </p>
            </div>

            <div>
              <Label>Tipo de Bloqueio *</Label>
              <Select 
                value={blockFormData.tipo} 
                onValueChange={(v) => setBlockFormData({...blockFormData, tipo: v})}
              >
                <SelectTrigger className="h-11 rounded-lg" data-testid="block-tipo-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dia">Dia</SelectItem>
                  <SelectItem value="semana">Semana (7 dias)</SelectItem>
                  <SelectItem value="quinzena">Quinzena (15 dias)</SelectItem>
                  <SelectItem value="mes">Mes (30 dias)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Data Início *</Label>
              <Input
                type="date"
                value={blockFormData.data_inicio}
                onChange={(e) => setBlockFormData({...blockFormData, data_inicio: e.target.value})}
                required
                className="h-11 rounded-lg"
                data-testid="block-data-inicio"
              />
            </div>

            {blockFormData.data_inicio && (
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-sm text-slate-700">
                  Período: <strong>{new Date(blockFormData.data_inicio + 'T12:00:00').toLocaleDateString('pt-BR')}</strong> até{' '}
                  <strong>{new Date(calcBlockEndDate(blockFormData.data_inicio, blockFormData.tipo) + 'T12:00:00').toLocaleDateString('pt-BR')}</strong>
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Sigla (opcional)</Label>
                <Select 
                  value={blockFormData.sigla || '__all__'} 
                  onValueChange={(v) => setBlockFormData({...blockFormData, sigla: v === '__all__' ? '' : v})}
                >
                  <SelectTrigger className="h-11 rounded-lg" data-testid="block-sigla-select">
                    <SelectValue placeholder="Todos os procedimentos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos os procedimentos</SelectItem>
                    {Object.entries(siglas)
                      .filter(([_, info]) => (info.origin || 'hospital') === originTab)
                      .map(([sig, info]) => (
                        <SelectItem key={sig} value={sig}>{sig} - {info.nome}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Horário (opcional)</Label>
                <Input
                  type="time"
                  value={blockFormData.horario}
                  onChange={(e) => setBlockFormData({...blockFormData, horario: e.target.value})}
                  placeholder="Ex: 12:30"
                  className="h-11 rounded-lg"
                  data-testid="block-horario"
                />
                <p className="text-[11px] text-slate-500 mt-1">Se preenchido, bloqueia apenas esse horário.</p>
              </div>
            </div>

            <div>
              <Label>Motivo</Label>
              <Input
                value={blockFormData.motivo}
                onChange={(e) => setBlockFormData({...blockFormData, motivo: e.target.value})}
                placeholder="Ex: recesso, férias, manutenção..."
                className="h-11 rounded-lg"
                data-testid="block-motivo"
              />
            </div>

            <div className="flex gap-3 justify-end pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => { setBlockDialogOpen(false); setEditingBlock(null); }}>
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="confirm-block-button"
              >
                <Lock className="h-4 w-4 mr-2" />
                {editingBlock ? 'Salvar' : 'Bloquear'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de Edicao de Vagas por Procedimento */}
      <Dialog open={editProcDialog} onOpenChange={(open) => { setEditProcDialog(open); if (!open) setEditingSlot(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center gap-2">
              <Pencil className="h-6 w-6 text-blue-600" />
              Vagas de {editProcSigla}
            </DialogTitle>
          </DialogHeader>

          <div className="mt-4">
            {editProcSlots.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <Calendar className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Nenhuma vaga encontrada para {editProcSigla} neste mes</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                {editProcSlots
                  .sort((a, b) => a.data.localeCompare(b.data) || a.horario.localeCompare(b.horario))
                  .map((slot) => (
                  <div key={slot.id} className={`p-3 rounded-lg border transition-colors ${editingSlot?.id === slot.id ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:bg-slate-50'}`}>
                    {editingSlot?.id === slot.id ? (
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Data</Label>
                            <Input
                              type="date"
                              value={editSlotForm.data}
                              onChange={(e) => setEditSlotForm({...editSlotForm, data: e.target.value})}
                              className="h-9"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Horario</Label>
                            <Input
                              type="time"
                              value={editSlotForm.horario}
                              onChange={(e) => setEditSlotForm({...editSlotForm, horario: e.target.value})}
                              className="h-9"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Quantidade</Label>
                            <Input
                              type="number"
                              min="1"
                              value={editSlotForm.quantidade_total}
                              onChange={(e) => setEditSlotForm({...editSlotForm, quantidade_total: parseInt(e.target.value) || 1})}
                              className="h-9"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Turno</Label>
                            <Select value={editSlotForm.turno} onValueChange={(v) => setEditSlotForm({...editSlotForm, turno: v})}>
                              <SelectTrigger className="h-9">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="M">Manha</SelectItem>
                                <SelectItem value="T">Tarde</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs">Profissional</Label>
                          <Input
                            value={editSlotForm.profissional}
                            onChange={(e) => setEditSlotForm({...editSlotForm, profissional: e.target.value})}
                            placeholder="Nome do profissional"
                            className="h-9"
                          />
                        </div>
                        <div className="flex gap-2 justify-end">
                          <Button size="sm" variant="outline" onClick={() => setEditingSlot(null)}>
                            Cancelar
                          </Button>
                          <Button size="sm" onClick={handleSaveSlotEdit} className="bg-blue-600 hover:bg-blue-700">
                            <Save className="h-3.5 w-3.5 mr-1" />
                            Salvar
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="text-center min-w-[70px]">
                            <p className="text-sm font-bold text-slate-900">{new Date(slot.data + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}</p>
                            <p className="text-xs text-slate-500">{slot.horario}</p>
                          </div>
                          <div>
                            <p className="text-sm text-slate-700">{slot.profissional || '—'}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs px-1.5 py-0.5 bg-teal-100 text-teal-700 rounded">{slot.turno === 'M' ? 'Manha' : 'Tarde'}</span>
                              <span className="text-xs text-slate-500">
                                {slot.quantidade_agendada || 0}/{slot.quantidade_total} ocupada(s)
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleStartEditSlot(slot)}
                            className="p-1.5 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                            title="Editar"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteSlotFromCard(slot.id)}
                            className="p-1.5 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                            title="Excluir"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end pt-4 border-t mt-4">
            <Button variant="outline" onClick={() => setEditProcDialog(false)}>
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
